import { useMetrics } from "@/api/hooks";
import { AppShell } from "@/components/app-shell";

/**
 * THE PLATFORM DASHBOARD.
 *
 * Every figure here is a COUNT across every tenant. Nothing on this page names a customer,
 * and nothing on it can: the endpoint behind it returns aggregates only, so there is no
 * shape of this screen that could quietly become a cross-tenant reader with a chart on top.
 */
export function MetricsPage() {
  const { data, isPending, error } = useMetrics();

  return (
    <AppShell>
      <h1 className="text-xl font-semibold">Platform</h1>
      <p className="mt-1 text-sm text-[var(--color-muted)]">
        Aggregates across every tenant. No customer's rows appear here.
      </p>

      {error && (
        <p role="alert" className="panel mt-6 p-4 text-sm text-[var(--color-dry)]">
          {error.message}
        </p>
      )}

      {isPending && <p className="mt-6 text-[var(--color-muted)]">Loading…</p>}

      {data && (
        <>
          <section className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Metric label="Organizations" value={data.organizations_total} hint="not deleted" />
            <Metric label="Active" value={data.organizations_active} tone="wet" />
            <Metric label="Suspended" value={data.organizations_suspended} tone="warn" />
            <Metric
              label="Active last 30d"
              value={data.active_organizations_last_30d}
              hint="recorded a refill"
            />
          </section>

          <section className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Metric label="Users" value={data.users_total} />
            <Metric label="Signups 30d" value={data.signups_last_30d} />
            <Metric label="Sites" value={data.sites_total} />
            <Metric label="Tanks" value={data.tanks_total} />
          </section>

          <section className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Metric label="Refills 30d" value={data.refills_last_30d} hint="the product working" />
          </section>

          <p className="tabular mt-6 text-xs text-[var(--color-faint)]">
            Generated {new Date(data.generated_at).toLocaleString()}
          </p>
        </>
      )}
    </AppShell>
  );
}

function Metric({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: number;
  hint?: string;
  tone?: "wet" | "warn";
}) {
  const toneClass =
    tone === "wet"
      ? "text-[var(--color-wet)]"
      : tone === "warn"
        ? "text-[var(--color-warn)]"
        : "text-[var(--color-text)]";

  return (
    <div className="panel p-4">
      <div className="text-xs uppercase tracking-wide text-[var(--color-faint)]">{label}</div>
      <div className={`tabular mt-2 text-3xl ${toneClass}`}>{value.toLocaleString()}</div>
      {hint && <div className="mt-1 text-xs text-[var(--color-faint)]">{hint}</div>}
    </div>
  );
}
